/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { routeTable } from 'src/core/utils/routeTable';
import { OpenLuisAppEvent } from 'src/luis/modules/apps/handlers/luisAppHandlerGroup';
import { LuisId } from 'src/luis/types/Resource';

/**
 * A hook used to initialize an application by doing the following.
 *
 * 1) Fetches the current app if it's not already fetched.
 * 2) Sets the initialized state to be true.
 */
export const useInitializeConversationApp = ({ appId }: { appId: LuisId }) => {
    const [initialized, setInitialized] = React.useState(false);

    const history = useHistory();
    const { dispatcher, rootStore } = useGlobalState();

    const currentConversationAppStore = rootStore.currentLuisAppStore;
    const currentSelectedCsStore = rootStore.azureStore.session.csAccountStore;
    const currentAppVersionStore = currentConversationAppStore?.currentAppVersionStore;

    React.useEffect(() => {
        const eventBus = getEventBusV2();

        return eventBus.on<OpenLuisAppEvent>({
            kind: 'openConversationApp',
            callback: params => {
                if (params.eventType === 'failed') {
                    history.push(routeTable.luis.applicationsPage());
                }

                const { trainingStore } = rootStore.currentLuisAppStore.currentAppVersionStore;
                if (trainingStore?.currentTrainingStatus.overallStatus === 'inProgress') {
                    dispatcher.dispatch('train', { context: { forceRefresh: true }, mode: trainingStore.lastTrainingMode });
                }
            }
        });
    }, []);

    React.useEffect(() => {
        if (currentConversationAppStore === null || currentConversationAppStore.id !== appId) {
            dispatcher.dispatch('openConversationApp', { context: { appId } });

            return;
        }

        if (currentAppVersionStore) {
            setInitialized(true);
        }
    }, [appId, currentConversationAppStore, currentAppVersionStore]);

    React.useEffect(() => () => dispatcher.dispatch('closeConversationApp'), []);

    // Redirects to welcome page if the current authoring resource doesn't have access to this app
    React.useEffect(() => {
        if (currentConversationAppStore?.csAccountPath && currentSelectedCsStore?.path !== currentConversationAppStore?.csAccountPath) {
            history.push(routeTable.luis.root());
        }
    }, [currentSelectedCsStore, currentConversationAppStore]);

    return initialized;
};
